import React, { useEffect, useRef, useState, useCallback } from "react";
import CheckoutDrawer from "../../common/CheckoutDrawer";
import { convertFromUTC } from "../../../util/convert_timezone";
import MessageAPI from "../../../services/messageService";

// ConversationDrawer — the per-job dealer↔specialist message thread, rendered as
// a CONSOLIDATED TIMELINE inside the reusable CheckoutDrawer shell.
//
// Timeline items are discriminated by `kind`:
//   "message" → a human bubble; right-aligned when authored by the current user
//               (sender_user_type === currentUserType), left otherwise.
//   "event"   → a centered, muted system line projected from request_state
//               (e.g. "Job scheduled"). Events are ambient — they never count
//               toward unread.
//
// Transport is async polling: while open the drawer re-fetches every POLL_MS and
// marks the thread read. No websockets.
//
// Props:
//   show              bool        — open/closed (parent owns)
//   onHide            () => void  — close
//   requestId         number|string
//   currentUserType   "dealer" | "installer"
//   currentUserId     number      — for optimistic bubble identity
//   otherPartyName    string      — the other party's name (shown in header)
//   vehicle           string      — vehicle line for the header (year/make/model…)
//   services          string      — service(s) line for the header
//   readOnly          bool        — dealer-group: view only, composer hidden
//   onReadStateChange (requestId) => void — fired after a successful mark-read so
//                                  parents can zero the unread chip locally

const POLL_MS = 10000;

const ConversationDrawer = ({
  show,
  onHide,
  requestId,
  currentUserType,
  currentUserId,
  otherPartyName,
  vehicle,
  services,
  readOnly = false,
  onReadStateChange,
}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState(null);

  const inFlight = useRef(false);
  const cancelled = useRef(false);
  const bodyRef = useRef(null);
  const endRef = useRef(null);

  // Merge a freshly-fetched list with local state, preserving optimistic temp
  // rows that the server hasn't echoed yet and replacing them once it has.
  const mergeMessages = useCallback((prev, incoming) => {
    const pending = prev.filter(
      (m) => m.optimistic && !incoming.some((s) => s.client_key && s.client_key === m.client_key)
    );
    return [...incoming, ...pending];
  }, []);

  const itemId = (m) => m.message_id ?? m.event_id;
  const lastMessageId = (list) => {
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i].kind === "message" && !list[i].optimistic) return list[i].message_id;
    }
    return null;
  };

  const load = useCallback(
    async (isInitial) => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        const list = await MessageAPI.listMessages(requestId);
        if (cancelled.current) return;
        setMessages((prev) => mergeMessages(prev, list));
        setError(null);
        const lastId = lastMessageId(list);
        if (lastId) {
          await MessageAPI.markRead(requestId, lastId, currentUserId, currentUserType);
          if (!cancelled.current && onReadStateChange) onReadStateChange(requestId);
        }
      } catch (e) {
        if (!cancelled.current) setError("Couldn’t load messages. Retrying…");
      } finally {
        inFlight.current = false;
        if (isInitial && !cancelled.current) setLoading(false);
      }
    },
    [requestId, currentUserId, currentUserType, mergeMessages, onReadStateChange]
  );

  // Open/close lifecycle: initial load + mark-read, then poll while open and
  // visible. Cleanup guards against React-17 unmounted-setState.
  useEffect(() => {
    if (!show || !requestId) return undefined;
    cancelled.current = false;
    setLoading(true);
    setMessages([]);
    load(true);
    const timer = setInterval(() => {
      if (!document.hidden) load(false);
    }, POLL_MS);
    return () => {
      cancelled.current = true;
      clearInterval(timer);
    };
  }, [show, requestId, load]);

  // Auto-scroll to the latest, but only when the user is already near the bottom
  // (don't yank them up while they scroll back through history).
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || !endRef.current) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const handleSend = async () => {
    const body = draft.trim();
    if (!body || sending || readOnly) return;
    const clientKey = `temp-${Date.now()}`;
    const optimistic = {
      kind: "message",
      message_id: clientKey,
      client_key: clientKey,
      optimistic: true,
      body,
      sender_user_type: currentUserType,
      sender_user_id: currentUserId,
      sender_name: "You",
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft("");
    setSending(true);
    try {
      const saved = await MessageAPI.postMessage(requestId, body, currentUserId, currentUserType);
      if (cancelled.current) return;
      setMessages((prev) =>
        prev.map((m) => (m.client_key === clientKey ? { ...saved, kind: "message" } : m))
      );
    } catch (e) {
      if (cancelled.current) return;
      setMessages((prev) =>
        prev.map((m) => (m.client_key === clientKey ? { ...m, failed: true } : m))
      );
      setError("Message failed to send.");
    } finally {
      if (!cancelled.current) setSending(false);
    }
  };

  const retry = (msg) => {
    setMessages((prev) => prev.filter((m) => m.client_key !== msg.client_key));
    setDraft(msg.body);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const composer = readOnly ? (
    <div className="jv-conv-readonly">
      <i className="fa-sharp fa-regular fa-eye mr-2" />
      Viewing only — group accounts can’t send messages.
    </div>
  ) : (
    <div className="jv-composer">
      {/* FUTURE (v2): mic button slots into this leftmost cell — no layout change.
          <button type="button" className="jv-composer-mic" onClick={onStartDictation}
            aria-label="Dictate"><i className="fa-sharp fa-regular fa-microphone" /></button> */}
      <textarea
        className="jv-composer-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Type a message…"
        rows={1}
        disabled={sending}
      />
      <button
        type="button"
        className="jv-composer-send"
        disabled={sending || !draft.trim()}
        onClick={handleSend}
        aria-label="Send"
      >
        <i className="fa-sharp fa-regular fa-paper-plane" />
      </button>
    </div>
  );

  return (
    <CheckoutDrawer
      show={show}
      onHide={onHide}
      title={vehicle || "Messages"}
      subtitle={
        <>
          {services ? <div className="jv-conv-subline">{services}</div> : null}
          {otherPartyName ? <div className="jv-conv-subline">{otherPartyName}</div> : null}
        </>
      }
      footer={composer}
      width={460}
    >
      <div className="jv-conv-body" ref={bodyRef}>
        {error ? (
          <div className="jv-conv-error" onClick={() => setError(null)}>
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="jv-conv-skeleton">
            <span className="jv-skel jv-skel-theirs" />
            <span className="jv-skel jv-skel-mine" />
            <span className="jv-skel jv-skel-theirs" />
          </div>
        ) : (
          messages.map((m) => {
            // System events render centered as a neutral grey pill. The creation
            // note is a real authored message, so it stays a party bubble below.
            if (m.kind === "event") {
              return (
                <div key={itemId(m)} className="jv-event">
                  <span className="jv-event-text">{m.text}</span>
                  <span className="jv-event-time">
                    {convertFromUTC(m.created_at).format("MMM D, h:mm a")}
                  </span>
                </div>
              );
            }
            // Alignment is viewer-relative (your own messages on the right, like
            // texting); colour is by PARTY (dealer = orange, specialist = blue)
            // to match the app's colour coding regardless of who's viewing.
            const mine = m.sender_user_type === currentUserType;
            const isDealer = m.sender_user_type === "dealer";
            return (
              <div
                key={itemId(m)}
                className={`jv-msg ${mine ? "jv-msg-mine" : "jv-msg-theirs"} ${
                  m.failed ? "jv-msg-failed" : ""
                }`}
              >
                {!mine ? <div className="jv-msg-sender">{m.sender_name}</div> : null}
                <div
                  className={`jv-msg-bubble ${isDealer ? "jv-bubble-dealer" : "jv-bubble-installer"}`}
                >
                  {m.body}
                </div>
                <div className="jv-msg-meta">
                  {convertFromUTC(m.created_at).format("MMM D, h:mm a")}
                  {m.failed ? (
                    <button type="button" className="jv-msg-retry" onClick={() => retry(m)}>
                      Failed — retry
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <style>{`
        .jv-conv-body { display: flex; flex-direction: column; gap: 10px; }
        .jv-conv-error {
          background: #FEF2F2; color: #B91C1C; border: 1px solid #FECACA;
          border-radius: 8px; padding: 8px 12px; font-size: 13px; cursor: pointer;
        }
        .jv-event { align-self: center; text-align: center; margin: 6px 0; max-width: 88%; }
        .jv-event-text {
          display: inline-block; font-size: 12.5px; color: #4B5563; font-weight: 500;
          background: #E5E7EB; border-radius: 12px; padding: 7px 13px; line-height: 1.35;
          white-space: pre-wrap; word-break: break-word;
        }
        .jv-event-time { display: block; font-size: 11px; color: #9CA3AF; margin-top: 2px; }
        .jv-msg { display: flex; flex-direction: column; max-width: 80%; }
        .jv-msg-theirs { align-self: flex-start; align-items: flex-start; }
        .jv-msg-mine { align-self: flex-end; align-items: flex-end; }
        .jv-msg-sender { font-size: 11px; color: #6B7280; margin: 0 4px 2px; }
        .jv-msg-bubble {
          padding: 9px 13px; border-radius: 14px; font-size: 14px; line-height: 1.35;
          white-space: pre-wrap; word-break: break-word; text-align: left; color: #fff;
        }
        .jv-msg-theirs .jv-msg-bubble { border-bottom-left-radius: 4px; }
        .jv-msg-mine .jv-msg-bubble { border-bottom-right-radius: 4px; }
        /* Party colours — matches the app's dealer=orange / installer=blue coding. */
        .jv-bubble-dealer { background: #fd7e14; }
        .jv-bubble-installer { background: #007bff; }
        .jv-msg-failed .jv-msg-bubble { opacity: .6; }
        .jv-msg-meta { font-size: 11px; color: #9CA3AF; margin: 2px 6px 0; }
        .jv-msg-retry {
          background: none; border: none; color: #B91C1C; font-size: 11px;
          cursor: pointer; padding: 0 0 0 6px;
        }
        .jv-conv-skeleton { display: flex; flex-direction: column; gap: 14px; }
        .jv-skel {
          height: 34px; border-radius: 14px; background: #EEF0F3;
          animation: jvskel 1.2s ease-in-out infinite;
        }
        .jv-skel-theirs { width: 60%; align-self: flex-start; }
        .jv-skel-mine { width: 50%; align-self: flex-end; }
        @keyframes jvskel { 0%,100% { opacity: .5; } 50% { opacity: 1; } }
        .jv-conv-readonly { font-size: 13px; color: #6B7280; text-align: center; }
        .jv-composer { display: flex; align-items: flex-end; gap: 8px; }
        .jv-composer-input {
          flex: 1; resize: none; max-height: 120px; min-height: 40px;
          border: 1px solid #D1D5DB; border-radius: 10px; padding: 9px 12px;
          font-size: 14px; line-height: 1.35; font-family: inherit;
        }
        .jv-composer-input:focus { outline: none; border-color: #2563EB; }
        .jv-composer-send {
          flex: 0 0 auto; width: 40px; height: 40px; border: none; border-radius: 10px;
          background: #2563EB; color: #fff; font-size: 16px; cursor: pointer;
        }
        .jv-composer-send:disabled { background: #9CA3AF; cursor: not-allowed; }
      `}</style>
    </CheckoutDrawer>
  );
};

export default ConversationDrawer;
